<template>
  <!-- Figma 2169:4704: overlay rgba(0,0,0,.4), รูปไม่มีมุมมน/เงา, ปุ่มปิด X ขาว 50px อยู่ "ใน" มุมขวาบนของรูป (ห่าง 10)
       overlay ซ่อนไว้ (visibility) จนรูปโหลดเสร็จ — ไม่โชว์ฉากมืดเปล่า + ปุ่มปิดกลางจอระหว่างรอรูป; รูปเสีย = ไม่แสดงเลย -->
  <div
    v-if="show && banner"
    class="banner-popup-overlay"
    :class="{ 'banner-popup-overlay--ready': imgReady }"
    role="dialog"
    aria-modal="true"
    data-testid="banner-popup"
    @click.self="dismiss"
  >
    <!-- box shrink-wraps the image so the close button always sits on the image
         corner, at any aspect ratio (portrait / landscape / square) -->
    <div class="banner-popup-box">
      <a
        v-if="banner.linkUrl"
        :href="banner.linkUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="banner-popup-link"
        @click="onLinkClick"
      >
        <img :src="imageSrc" :alt="banner.altText || ''" class="banner-popup-img" @load="onImgLoad" @error="onImgError" />
      </a>
      <img v-else :src="imageSrc" :alt="banner.altText || ''" class="banner-popup-img" @load="onImgLoad" @error="onImgError" />

      <button
        type="button"
        class="banner-popup-close"
        :aria-label="$t('cancel')"
        data-testid="banner-popup-close"
        @click="dismiss"
      >
        <svg viewBox="0 0 24 24" width="50" height="50" aria-hidden="true">
          <path
            fill="currentColor"
            d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
        <v-tooltip activator="parent" location="top">{{ $t('close') }}</v-tooltip>
      </button>
    </div>
  </div>
</template>

<script>
import { bannerImageSrc } from '@/utils/apiAssetUrl';

// 'chat' = หน้าแชท /FullStreamV2 แบบเปิดตรง (ไม่ใช่ widget ที่ฝัง) — backend เพิ่มใน ValidPageKeys คู่กัน
const VALID_PAGE_KEYS = new Set(['login', 'product_admin', 'portal', 'fullsnippet', 'landing', 'chat']);

// /FullStreamV2 เป็นทั้งหน้าแชทพอร์ทัลและ iframe ของ widget (SmallV2/FullV2 loader ใส่ snippet=true)
// และแท็บ MS Teams (roleId 5) — banner ของ widget แสดงโดย loader บนหน้าเว็บลูกค้าแล้ว (key fullsnippet)
// จึงต้องไม่แสดงซ้ำใน iframe; อ่านทั้ง route query และ location.search (key ไม่สนตัวพิมพ์) แบบเดียวกับ
// FullStreamReDesignV2.readUrlParams()
function readQueryFlag(query, name) {
  const want = name.toLowerCase();
  for (const key of Object.keys(query || {})) {
    if (key.toLowerCase() === want) {
      const raw = query[key];
      return String(Array.isArray(raw) ? raw[0] : raw);
    }
  }
  try {
    for (const [key, val] of new URLSearchParams(globalThis.location?.search || '')) {
      if (key.toLowerCase() === want) return String(val);
    }
  } catch {
    // intentionally ignored: malformed search string — treat the flag as absent
  }
  return '';
}

function isInIframe() {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    return true; // cross-origin parent blocks access → we are framed
  }
}

export default {
  name: 'BannerPopup',

  data() {
    return {
      loaded: false,
      enabled: false,
      targetPages: [],
      banner: null,
      show: false,
      imgReady: false,
      // Pages already shown during this app load (full page session). Reset on refresh
      // because the component remounts. Ensures the banner shows once per page per load
      // and does NOT re-pop when navigating back to a page within the SPA.
      shownPages: {},
    };
  },

  computed: {
    imageSrc() {
      return bannerImageSrc(this.banner);
    },

    pageKey() {
      const route = this.$route || {};
      // Each page key maps to the FIRST/landing route of that area only, so the banner
      // shows on that page's initial load and not on every sub-page of the area.
      if (route.name === 'Login') return 'login';
      if (route.name === 'Landing') return 'landing'; // public home page
      if (route.name === 'dashboard') return 'portal'; // ChatFramework portal home (chat)
      if (route.name === 'SuperAdminDashboard') return 'product_admin'; // Product Admin home
      // หน้าแชท /FullStreamV2 เปิดตรง (พอร์ทัล/สาธารณะ) เท่านั้น — widget/Teams ข้าม (ดู isEmbeddedChat)
      if (route.name === 'FullStreamV2') return this.isEmbeddedChat(route) ? null : 'chat';
      // Note: the 'fullsnippet' page is handled by the snippet loader
      // (public/Snippet/ChatFrameworkSnippetFullV2.js) on the host page, not here,
      // to avoid a duplicate popup inside the chat iframe.
      return null;
    },
  },

  // Re-evaluate when the route changes (SPA navigation). recompute() only shows the
  // banner the FIRST time each configured page is reached per app load, so navigating
  // away and back does not pop it again — only a refresh (remount) does.
  watch: {
    '$route.name'(newName, oldName) {
      if (newName !== oldName) this.recompute();
    },
  },

  mounted() {
    this.fetchCurrent();
  },

  methods: {
    fetchCurrent() {
      this.$call.WebContentBannerPopupCurrent(
        (res) => {
          this.loaded = true;
          if (res && res.status === 0 && res.data) {
            this.enabled = !!res.data.enabled;
            this.targetPages = Array.isArray(res.data.targetPages)
              ? res.data.targetPages.filter((p) => VALID_PAGE_KEYS.has(p))
              : [];
            this.banner = res.data.banner || null;
          }
          this.recompute();
        },
        () => {
          this.loaded = true;
        }
      );
    },

    recompute() {
      const key = this.pageKey;
      const eligible =
        this.loaded &&
        this.enabled &&
        !!this.banner &&
        !!key &&
        this.targetPages.includes(key);

      if (eligible && !this.shownPages[key]) {
        this.shownPages[key] = true; // mark as shown for this app load
        // overlay ยังเปิดค้าง (เช่น กด back ไปหน้าอื่นที่ตั้งไว้) = <img> เดิมโหลดแล้ว ไม่มี load ใหม่ → คง imgReady
        if (!this.show) this.imgReady = false;
        this.show = true;
      } else {
        // Not eligible, or already shown earlier this load (navigated back) → keep hidden.
        this.show = false;
      }
    },

    isEmbeddedChat(route) {
      const query = route.query || {};
      if (readQueryFlag(query, 'snippet').toLowerCase() === 'true') return true;
      if (this.$store?.state?.Snippet_StatusData) return true;
      const role = readQueryFlag(query, 'roleid') || readQueryFlag(query, 'role') || readQueryFlag(query, 'usertype');
      if (role === '5') return true; // MS Teams
      return isInIframe();
    },

    onImgLoad() {
      this.imgReady = true;
    },

    onImgError() {
      // รูปโหลดไม่ได้ — ไม่แสดงฉากมืดเปล่า (ถือว่าแสดงแล้วรอบนี้ ไม่ลองซ้ำจนกว่าจะ refresh)
      this.show = false;
    },

    dismiss() {
      // Hide for the current view only. Already marked shown for this load, so it won't
      // re-pop on SPA navigation; it reappears on a full refresh (remount resets state).
      this.show = false;
    },

    onLinkClick() {
      this.dismiss();
    },
  },
};
</script>

<style scoped>
.banner-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.4);
  box-sizing: border-box;
  visibility: hidden;
}

.banner-popup-overlay--ready {
  visibility: visible;
}

.banner-popup-box {
  position: relative;
  display: block;
  max-width: 100%;
  max-height: 100%;
  line-height: 0;
}

.banner-popup-link {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.banner-popup-img {
  display: block;
  /* Cap against the viewport (minus a hardcoded edge gap), not parent percentages.
     Nested max-height:100% does not resolve when the box height is auto, so tall
     (portrait) images overflowed short screens. vw/vh are viewport-relative and
     always resolve, so the image fits on every screen and keeps the edge gap. */
  max-width: calc(100vw - 2 * clamp(20px, 5vmin, 56px));
  max-height: calc(100vh - 2 * clamp(20px, 5vmin, 56px));
}

/* X ขาว 50px ไม่มีพื้น อยู่ในมุมขวาบนของรูป (Figma: x708 y10 บนรูป 768) — เงาจางที่ตัว X
   กันมองไม่เห็นเมื่อแอดมินอัปโหลดรูปพื้นสว่าง */
.banner-popup-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  width: 50px;
  height: 50px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  transition: opacity 0.15s ease;
}

.banner-popup-close:hover {
  opacity: 0.8;
}

.banner-popup-close:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: -2px;
}
</style>
